import { create } from 'zustand'

export const THEMES = [
  { id: 'classic', name: 'Classic', description: 'Clean and calm', swatch: ['#1e293b', '#10b981', '#ddd6fe'] },
  { id: 'newsprint', name: 'Newsprint', description: 'Ink on paper', swatch: ['#1c1b19', '#0f766e', '#f4efe4'] },
  { id: 'arcade', name: 'Arcade', description: 'Bright and bouncy', swatch: ['#4f46e5', '#a3e635', '#d946ef'] },
] as const

export type Theme = (typeof THEMES)[number]['id']

export const DEFAULT_THEME: Theme = 'classic'
const KEY = 'wordevery.theme'

// Browser bar colour per theme, light then dark. public/theme.js keeps a copy.
const BAR_COLORS: Record<Theme, [string, string]> = {
  classic: ['#f1f5f9', '#020617'],
  newsprint: ['#f4efe4', '#15171c'],
  arcade: ['#eef2ff', '#0b0a1f'],
}

export function isTheme(value: unknown): value is Theme {
  return THEMES.some((t) => t.id === value)
}

export function loadTheme(): Theme {
  try {
    const saved = localStorage.getItem(KEY)
    return isTheme(saved) ? saved : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

export function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme
  const [light, dark] = BAR_COLORS[theme]
  document.querySelector('meta[name="theme-color"][media*="light"]')?.setAttribute('content', light)
  document.querySelector('meta[name="theme-color"][media*="dark"]')?.setAttribute('content', dark)
}

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

export const useTheme = create<ThemeState>((set) => ({
  theme: loadTheme(),
  setTheme: (theme) => {
    applyTheme(theme)
    try {
      localStorage.setItem(KEY, theme)
    } catch {
      // Private mode: the choice lasts for this visit.
    }
    set({ theme })
  },
}))
